import { useCallback, useEffect, useMemo, useState } from "react";
import styles from "./FilterSliderNew.module.css";
import "../../../src/components/DealsPage/CustomCategoriesFilter.css";
import FilterAccordion from "@components/Accordion/FilterAccordion";
import RangeSlider from "@components/RangeSlider/RangeSlider";
import RatingSelector from "@components/RatingSelector/RatingSelector";
import { useSearchParams } from "node_modules/next/navigation";
import { useAppContext } from "@components/Provider";
import { ageRanges } from "src/constants/DealsPageConstants";
import Post from "src/constants/Post";

// GTM tracking functions
const fireGTMFilterButtonClick = (buttonName) => {
  if (typeof window !== "undefined") {
    window.dataLayer.push({
      event: `${buttonName}ButtonClick`,
    });
  }
};

const fireGTMFilterApply = ({
  genres,
  retailers,
  price,
  rating,
  authorTitle = null,
  ageRange = null,
}) => {
  if (typeof window !== "undefined") {
    window.dataLayer = window.dataLayer || [];
    // Clear previous filter values using _clear flag
    window.dataLayer.push({
      event: "FilterApply",
      "genre-filter": genres?.length > 0 ? genres : null,
      "retailer-filter": retailers?.length > 0 ? retailers : null,
      "price-filter": price > 0 ? `0 - ${price}` : 0,
      "rating-filter": rating > 0 ? `${rating} - 5` : null,
      "author-title-filter": authorTitle,
      "age-range-filter": ageRange,
      _clear: true,
    });
  }
};

const FilterSliderNew = ({
  filters,
  selectedFilters,
  setSelectedFilters,
  showMenu,
  setShowMenu,
  defaultOpenAccordion,
  setDefaultOpenAccordion,
  postType = null,
  retailers,
}) => {
  const { router } = useAppContext();
  const slug = router.asPath && router.asPath?.split("/").pop();

  const searchParams = useSearchParams();
  const retailer = searchParams.get("retailer");
  const cat = searchParams.get("cat");
  const isbn = searchParams.get("isbn");

  const [filterIsbn, setFilterIsbn] = useState("");
  const [selectedGenres, setSelectedGenres] = useState(selectedFilters.genres || []);
  const [selectedRetailers, setSelectedRetailers] = useState(selectedFilters.retailers || []);
  const [selectedAgeRanges, setSelectedAgeRanges] = useState(selectedFilters.ageRanges || []);
  const [selectedPrice, setSelectedPrice] = useState(selectedFilters.price || 5);
  const [selectedRating, setSelectedRating] = useState(selectedFilters.rating || 0);

  const [searchTerm, setSearchTerm] = useState("");

  // State to track open accordions
  const [openAccordion, setOpenAccordion] = useState(defaultOpenAccordion);
  const [initialFiltersProcessed, setInitialFiltersProcessed] = useState(false);

  useEffect(() => {
    setOpenAccordion(defaultOpenAccordion);
  }, [defaultOpenAccordion]); // Runs whenever `defaultOpenAccordion` changes

  useEffect(() => {
    if ((cat || retailer || isbn) && !initialFiltersProcessed) {
      if (isbn) {
        setFilterIsbn(isbn);
      }

      const retailerList = retailer?.split(",").map((r) => r.trim().toLowerCase()) || [];

      const validRetailers = retailers.filter(
        ({ key, value }) => retailerList.includes(key.toLowerCase()) || retailerList.includes(value)
      );

      const validRetailerValues = validRetailers.map(({ value }) => value);
      //when splitting categories, ignore commas followed by spaces
      //this is to avoid splitting "Graphic Novels, Manga & Comics" into "Graphic Novels" and "Manga & Comics"
      const categoryList =
        cat
          ?.split(/,(?!\s)/)
          .map((item) => item.trim())
          .map((c) => c.trim().toLowerCase()) || [];

      setSelectedGenres(categoryList);
      setSelectedRetailers(validRetailerValues);

      setSelectedFilters((prevFilters) => ({
        ...prevFilters,
        isUserAppliedFilter: false,
      }));

      setInitialFiltersProcessed(true); // Mark as processed, so it doesn't keep running
    } else {
      setSelectedFilters((prevFilters) => ({
        ...prevFilters,
        isUserAppliedFilter: true,
      }));
    }
  }, [retailer, cat, retailers, isbn]); // ✅ Added `retailers` dependency for consistency

  useEffect(() => {
    if (initialFiltersProcessed) {
      const isUserAppliedFilter = cat || retailers || isbn ? false : true;
      applyFilters(isUserAppliedFilter); // Run only after state updates
      setInitialFiltersProcessed(false); // Reset to avoid infinite calls
    }
  }, [initialFiltersProcessed]); // ✅ Now runs only when `initialFiltersProcessed` is set to true

  // Add effect to update local states when slider is opened
  useEffect(() => {
    if (showMenu) {
      setSelectedGenres(selectedFilters.genres || []);
      setSelectedRetailers(selectedFilters.retailers || []);
      setSelectedAgeRanges(selectedFilters.ageRanges || []);
      setSelectedPrice(selectedFilters.maxPrice || 5);
      setSelectedRating(selectedFilters.rating || 0);
      setFilterIsbn(selectedFilters.isbn || "");
      setSearchTerm(""); // Reset search term when opening slider
    }
  }, [showMenu, selectedFilters]);

  const handleSearchChange = useCallback(
    (e) => {
      const newValue = e.target.value;
      setSearchTerm(newValue);

      // If search is cleared (empty string), fire GTM FilterApply event
      if (!newValue) {
        // Clear the search term in selectedFilters
        setSelectedFilters((prevFilters) => ({
          ...prevFilters,
          searchTerm: null, // Set to null when cleared
          isUserAppliedFilter: true,
        }));

        // Fire GTM event with null author-title-filter
        fireGTMFilterApply({
          genres: selectedGenres,
          retailers: selectedRetailers,
          price: selectedPrice,
          rating: selectedRating,
          authorTitle: null, // Set to null when cleared
          ageRange: selectedAgeRanges?.length > 0 ? selectedAgeRanges : null,
        });
      }
    },
    [selectedGenres, selectedRetailers, selectedPrice, selectedRating, selectedAgeRanges]
  );

  const filteredGenres = useMemo(() => {
    return filters.customCatagories?.filter((category) =>
      category.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [filters.customCatagories, searchTerm]);

  const toggleAccordion = (key) => {
    setOpenAccordion((prev) => {
      const newKey = prev === key ? null : key;
      // Track filter button clicks
      if (newKey) {
        switch (newKey) {
          case "Genres":
            fireGTMFilterButtonClick("GenreFilter");
            break;
          case "Retailers":
            fireGTMFilterButtonClick("RetailersFilter");
            break;
          case "Rating":
            fireGTMFilterButtonClick("RatingFilter");
            break;
        }
      }
      return newKey;
    });
  };

  const handleGenreChange = (category) => {
    setSelectedGenres((prevGenres) => {
      const isSelected = prevGenres.includes(category);
      const updatedGenres = isSelected
        ? prevGenres.filter((g) => g !== category)
        : [...prevGenres, category];

      // Call GTM event function
      fireBookCategoryFilterClickGTMEvent({
        categoryName: category,
        selected: !isSelected,
      });

      return updatedGenres;
    });
  };

  const handleRetailersChange = (value) => {
    setSelectedRetailers((prevRetailers) =>
      prevRetailers.includes(value)
        ? prevRetailers.filter((r) => r !== value)
        : [...prevRetailers, value]
    );
  };

  const handleAgeRangeChange = (value) => {
    setSelectedAgeRanges((prevAgeRanges) => {
      const isSelected = prevAgeRanges.includes(value);
      const updatedAgeRanges = isSelected
        ? prevAgeRanges.filter((r) => r !== value)
        : [...prevAgeRanges, value];

      // Call GTM event function
      fireBookAgeRangeFilterClickGTMEvent({
        age: value,
        selected: !isSelected,
      });

      return updatedAgeRanges;
    });
  };

  const applyFilters = (isUserAppliedFilter = false) => {
    if (
      selectedGenres.length > 0 ||
      selectedRetailers.length > 0 ||
      selectedAgeRanges.length > 0 ||
      selectedPrice >= 0 ||
      selectedRating > 0 ||
      filterIsbn ||
      searchParams.searchParams
    ) {
      setSelectedFilters((prevFilters) => ({
        ...prevFilters,
        genres: selectedGenres,
        retailers: selectedRetailers,
        ageRanges: selectedAgeRanges,
        isbn: filterIsbn,
        minPrice: 0,
        maxPrice: selectedPrice,
        rating: selectedRating,
        // Preserve existing searchTerm from FilterComponentNew
        searchTerm: prevFilters.searchTerm,
        isUserAppliedFilter,
      }));
    } else {
      clearAllFilters();
    }

    // Fire GTM FilterApply event with current filter values
    fireGTMFilterApply({
      genres: selectedGenres,
      retailers: selectedRetailers,
      price: selectedPrice,
      rating: selectedRating,
      authorTitle: selectedFilters.searchTerm, // Use only the searchTerm from FilterComponentNew
      ageRange: selectedAgeRanges?.length > 0 ? selectedAgeRanges : null,
    });

    setShowMenu(false);
    setDefaultOpenAccordion("");
  };

  const clearAllFilters = () => {
    // Reset all states to their default values
    setSelectedGenres([]); // Reset selected genres to default (empty array)
    setSelectedRetailers([]); // Reset selected retailers to default (empty array)
    setSelectedAgeRanges([]); // Reset selected age range to default (empty array)
    setSelectedPrice(5); // Reset selected price to default (0)
    setSelectedRating(0); // Reset selected rating to default (0)

    // Reset the filters in the parent component while preserving searchTerm
    setSelectedFilters((prevState) => ({
      ...prevState,
      genres: [],
      retailers: [],
      ageRanges: [],
      minPrice: 0,
      maxPrice: 5,
      rating: 0,
      // Preserve existing searchTerm from FilterComponentNew
      searchTerm: prevState.searchTerm,
      isUserAppliedFilter: true,
    }));

    // Close the filter menu and reset accordion
    setShowMenu(false);
    setDefaultOpenAccordion("");
  };

  const fireBookCategoryFilterClickGTMEvent = ({ categoryName, selected }) => {
    if (typeof window !== "undefined") {
      if (categoryName) {
        const eventObj = {
          event: "BookCategoryFilterClick",
          bookCategoryFilter: categoryName,
          bookCategoryFilterState: selected ? "Selected" : "Deselected",
        };
        window.dataLayer.push(eventObj);
      }
    }
  };

  const fireBookAgeRangeFilterClickGTMEvent = ({ age, selected }) => {
    if (typeof window !== "undefined") {
      if (age) {
        const eventObj = {
          event: "BookAgeFilterClick",
          bookAgeFilter: age,
          bookAgeFilterState: selected ? "Selected" : "Deselected",
        };
        window.dataLayer.push(eventObj);
      }
    }
  };

  const handleCloseSlider = () => {
    setShowMenu(false);
    // Track All Filters button click when closing
    fireGTMFilterButtonClick("AllFilters");
  };

  return (
    <section className={`${styles["body"]} ${showMenu && styles["showDrawer"]}`}>
      <div className={`${styles["modal"]} ${styles["drawer"]}`}>
        <div className={styles["modalHeader"]}>
          <h3>Filter</h3>
          <button onClick={handleCloseSlider}>
            <img src="/icons/close.svg" alt="Close" width={20} height={20} />
          </button>
        </div>
        <div className={`${styles["modalBody"]} ${styles["filerSection"]}`}>
          {slug === "cheap-kids-books" && (
            <FilterAccordion
              title="Age"
              key="Age"
              isActive={openAccordion === "Age"}
              toggleAccordion={() => toggleAccordion("Age")}
            >
              <div>
                <div className={styles.checkboxList}>
                  {ageRanges.map(({ label, value, postfix }) => (
                    <div key={value} className={styles.checkboxListItem}>
                      <label className={styles.filterContainer}>
                        <div className={styles.filterTypeText}>{`${label} ${postfix}`}</div>
                        <input
                          type="checkbox"
                          checked={selectedAgeRanges.includes(value)}
                          onChange={() => handleAgeRangeChange(value)}
                        />
                        <span className={styles.checkmark}></span>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </FilterAccordion>
          )}
          <FilterAccordion
            title="Genres"
            key="genres-filter"
            isActive={openAccordion === "Genres"}
            toggleAccordion={() => toggleAccordion("Genres")}
          >
            <div>
              {/* Search Bar */}
              <div className={styles.filterSearchBox}>
                <div className={styles.filterSearch}>
                  <input
                    type="text"
                    className={styles.filterSearchTerm}
                    placeholder="Search by genre"
                    value={searchTerm}
                    onChange={handleSearchChange}
                  />
                </div>
              </div>

              {/* Filtered Genre List */}
              <div className={styles.checkboxList}>
                <b>All Genres</b>

                {/* Add All / Clear All Toggle */}
                <div className={styles.checkboxListItem}>
                  <label className={styles.filterContainer}>
                    <div className={styles.filterTypeText}>
                      {selectedGenres.length === filteredGenres.length ? "Clear All" : "Select All"}
                    </div>
                    <input
                      type="checkbox"
                      checked={selectedGenres.length === filteredGenres.length} // Check if all are selected
                      onChange={() => {
                        if (selectedGenres.length === filteredGenres.length) {
                          setSelectedGenres([]); // Clear all
                        } else {
                          setSelectedGenres(filteredGenres.map((genre) => genre.toLowerCase())); // Select all
                        }
                      }}
                    />
                    <span className={styles.checkmark}></span>
                  </label>
                </div>

                {/* Genre List */}
                {filteredGenres?.map((category, index) => (
                  <div key={index} className={styles.checkboxListItem}>
                    <label className={styles.filterContainer}>
                      <div className={styles.filterTypeText}>{category}</div>
                      <input
                        type="checkbox"
                        checked={selectedGenres.includes(category.toLowerCase())}
                        onChange={() => handleGenreChange(category.toLowerCase())}
                      />
                      <span className={styles.checkmark}></span>
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </FilterAccordion>

          {postType !== Post.TYPES.KINDLE_UNLIMITED_DEALS && (
            <FilterAccordion
              title="Retailers"
              key="Retailers"
              isActive={openAccordion === "Retailers"}
              toggleAccordion={() => toggleAccordion("Retailers")}
            >
              <div>
                <div className={styles.checkboxList}>
                  {retailers.map(({ key, value }) => (
                    <div key={value} className={styles.checkboxListItem}>
                      <label className={styles.filterContainer}>
                        <div className={styles.filterTypeText}>{key}</div>
                        <input
                          type="checkbox"
                          checked={selectedRetailers.includes(value)}
                          onChange={() => handleRetailersChange(value)}
                        />
                        <span className={styles.checkmark}></span>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </FilterAccordion>
          )}

          <FilterAccordion
            title="Price"
            key="Price"
            isActive={openAccordion === "Price"}
            toggleAccordion={() => toggleAccordion("Price")}
          >
            <div>
              <h5 className={styles.filterDescription}>Show all ebooks under:</h5>
              <RangeSlider
                step={1}
                defaultValue={selectedPrice}
                setSelectedPrice={setSelectedPrice}
              />
            </div>
          </FilterAccordion>

          <FilterAccordion
            title="Rating"
            key="Rating"
            isActive={openAccordion === "Rating"}
            toggleAccordion={() => toggleAccordion("Rating")}
          >
            <div>
              <h5 className={styles.filterDescription}>Only show ebooks with a rating of:</h5>
              <RatingSelector selectedRating={selectedRating} onRatingSelect={setSelectedRating} />
            </div>
          </FilterAccordion>
        </div>

        <div className={styles["modalFooter"]}>
          <button type="button" className={styles["modalButtonPrimary"]} onClick={clearAllFilters}>
            Clear All
          </button>
          <button type="button" className={styles["modalButtonSecondary"]} onClick={applyFilters}>
            Search
          </button>
        </div>
      </div>

      <div className={styles["overlay"]} onClick={() => setShowMenu(false)}></div>
    </section>
  );
};

export default FilterSliderNew;
